import { InternalEventType } from '../../../domain/enums/internal-event-type';
import { Referrer } from '../../../../interfaces/enums/referrer';
import globalStore from '@/store/global';
import globals from '@/utils/globals';
import { rollbar } from '@/utils/rollbar/rollbar';
import { GoogleLoginStatus, GoogleYoloResult, GSIClient, GSIDismissedReason, GSIId, GSINotification, GSIRenderButtonOptionsTheme } from './types';
import { GoogleButtonSize } from '@/domain/enums/google-button-size';

declare const google: GSIClient;
const GOOGLE_RETRY_LIMIT = 4;
const GOOGLE_RETRY_DELAY = 200;
const GOOGLE_RETRY_DELAY_MAX = 5000;
const YOLO_PROMPT_TIMEOUT_MS = 10000;

type SettleFunction = (result: GoogleYoloResult) => boolean;
type GoogleInstanceCallback = (response: any) => void;
type CredentialCallback = (credential: string) => void | Promise<void>;

export class GoogleClient {
	private googleLoadFailCount: number = 0;
	private loadInitializeSDKPromise: Promise<void> | null = null;
	private sdkLoaded: boolean = false;
	private sdkFailed: boolean = false;
	private gsiScript: HTMLScriptElement | null = null;
	private isGSIInitialized: boolean = false;
	private credentialCallback: CredentialCallback | null = null;
	constructor(private clientId: string) {}

	private awaitedLoad(): Promise<void> {
		if (this.loadInitializeSDKPromise) return this.loadInitializeSDKPromise;

		this.loadInitializeSDKPromise = this.initializeSDK();
		return this.loadInitializeSDKPromise;
	}

	public async showYolo(): Promise<GoogleYoloResult> {
		await this.awaitedLoad();

		return new Promise((resolve) => {
			if (!this.googleInstance) {
				resolve({ status: 'error', error: new Error('Google SDK unavailable') });
				return;
			}

			let settled = false;
			let timeoutHandle: ReturnType<typeof setTimeout> | undefined = undefined;
			const settle = (result: GoogleYoloResult): boolean => {
				if (settled) return false;
				settled = true;
				if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
				resolve(result);
				return true;
			};

			// Always initialize the GSI client so the Google Button has a valid
			// callback registered, even when the One Tap prompt is skipped.
			this.initializeYolo(settle);

			if (this.shouldSkipSigninPrompt) {
				settle({ status: 'skipped' });
				return;
			}

			timeoutHandle = setTimeout(() => {
				settle({ status: 'error', error: new Error('YOLO prompt timeout') });
			}, YOLO_PROMPT_TIMEOUT_MS);

			globals.eventBus.emit(InternalEventType.YOLO_PROMPT_REQUESTED);
			// Pre-set the YOLO referrer so it is recorded if the user accepts the
			// One Tap prompt. The Google Button's own click_listener overwrites
			// this with the button referrer if the user clicks the button instead.
			globalStore.mutations.setReferrerGoogleAuth(Referrer.GOOGLE_YOLO);
			this.googleInstance.prompt((notification) => this.emitYoloStatus(notification, settle));
		});
	}

	public async initializeSDK(): Promise<void> {
		if (this.sdkLoaded || this.sdkFailed) return;
		if (this.loadInitializeSDKPromise) return this.loadInitializeSDKPromise;

		this.loadInitializeSDKPromise = new Promise((resolve, reject) => this.tryLoadSDK(resolve, reject));
		return this.loadInitializeSDKPromise;
	}

	private tryLoadSDK(resolve: () => void, reject: (reason?: any) => void): void {
		if (this.isGoogleSDKAlreadyLoaded()) {
			this.handleScriptLoad(resolve);
			return;
		}

		this.cleanupPreviousScript();
		this.injectGSIScript();

		this.gsiScript!.onload = (): void => this.handleScriptLoad(resolve);
		this.gsiScript!.onerror = (): void => {
			try {
				this.handleScriptError(resolve, reject);
			} catch {
				//ignored so it doesn't fire on Rollbar
			}
		};
	}

	private cleanupPreviousScript(): void {
		if (!this.gsiScript) return;

		this.gsiScript.onload = null;
		this.gsiScript.onerror = null;
		document.head.removeChild(this.gsiScript);
		this.gsiScript = null;
	}

	private injectGSIScript(): void {
		this.gsiScript = document.createElement('script');
		this.gsiScript.src = 'https://accounts.google.com/gsi/client';
		this.gsiScript.async = false;
		document.head.appendChild(this.gsiScript);
	}

	private handleScriptLoad(resolve: () => void): void {
		if (this.sdkLoaded || this.sdkFailed) return;

		this.sdkLoaded = true;
		resolve();
		this.loadInitializeSDKPromise = null;
	}

	private handleScriptError(resolve: () => void, reject: (reason?: any) => void): void {
		if (this.sdkLoaded || this.sdkFailed) return;

		this.googleLoadFailCount++;

		if (this.googleLoadFailCount > GOOGLE_RETRY_LIMIT) {
			this.sdkFailed = true;

			reject(new Error('Google SDK failed to load after max retries.'));
			this.loadInitializeSDKPromise = null;
			return;
		}

		const delayMs = Math.min(GOOGLE_RETRY_DELAY * (2 ** this.googleLoadFailCount), GOOGLE_RETRY_DELAY_MAX);
		setTimeout(() => this.tryLoadSDK(resolve, reject), delayMs);
	}

	public async renderGoogleButton(
		options: { element: Element; template: string, gButtonSize?: GoogleButtonSize,
			theme?: GSIRenderButtonOptionsTheme },
		referrer: Referrer | null,
		onCredential?: CredentialCallback,
	): Promise<void> {
		await this.awaitedLoad();

		if (!this.googleInstance) return;
		if (!options.element) return;
		if (!options.element.parentElement) return;

		this.credentialCallback = onCredential ?? null;

		if (!this.isGSIInitialized) {
			this.initializeGoogleInstance((response) => {
				const credential: unknown = response.credential;
				if (typeof credential !== 'string' || credential.length === 0) {
					rollbar?.error('google_credential_unexpected_shape', { shape: typeof credential });
					return;
				}
				void this.handleCredential(credential);
			}, this.googleInstance, options.element.parentElement.id);

			this.isGSIInitialized = true;
		}

		this.googleInstance.renderButton(options.element as HTMLElement, {
			type: options.template === "Small" ? "icon" : "standard",
			shape: options.template === "Small" ? "square" : "rectangular",
			text: "signin_with",
			theme: options.theme || "filled_blue",
			size: options.gButtonSize || GoogleButtonSize.MEDIUM,
			width: this.getSiblingButtonWidth(options.element),
			logo_alignment: "left",
			locale: "en",
			click_listener: () => {
				globalStore.mutations.setReferrerGoogleAuth(referrer);
			},
		});
	}

	private emitYoloStatus(notification: GSINotification, settle: SettleFunction): void {
		const status: GoogleLoginStatus = GoogleClient.getGoogleLoginStatus(notification);

		switch (status) {
		case GoogleLoginStatus.SKIPPED:
			globals.eventBus.emit(InternalEventType.YOLO_SKIPPED);
			settle({ status: 'skipped' });
			break;
		case GoogleLoginStatus.DISMISSED:
			globals.eventBus.emit(InternalEventType.YOLO_DISMISSED);
			settle({ status: 'dismissed' });
			break;
		}
	}

	private getSiblingButtonWidth(element: Element): number {
		const siblingButton: Element | null = element.parentNode?.querySelector('.Provider') ?? null;

		if (!siblingButton) return 400;

		return Math.round(siblingButton.getBoundingClientRect().width);
	}

	private initializeGoogleInstance(
		callback: GoogleInstanceCallback,
		googleInstance: GSIId,
		parentId?: string): void
	{
		googleInstance.initialize({
			client_id: this.clientId,
			callback,
			context: "signin",
			ux_mode: "popup",
			auto_select: false,
			prompt_parent_id: parentId,
			cancel_on_tap_outside: false,
			itp_support: true,
			use_fedcm_for_button: true,
		});
	}

	private initializeYolo(settle: SettleFunction, parentId?: string): void {
		if (!this.googleInstance) return;

		this.initializeGoogleInstance((response) => {
			const credential: unknown = response.credential;
			if (typeof credential !== "string" || credential.length === 0) {
				const shape = typeof credential;
				rollbar?.error("google_credential_unexpected_shape", { shape });
				globals.eventBus.emit(InternalEventType.YOLO_ERROR);
				settle({
					status: "error",
					error: new Error(
						`invalid_google_credential_shape: received ${shape}`,
					),
				});
				return;
			}
			globals.eventBus.emit(InternalEventType.YOLO_CREDENTIAL_RETURNED);

			// The Google SDK reuses this single `initialize` callback for both
			// the One Tap (YOLO) prompt and the Google Button. If the YOLO
			// promise is still pending the caller of `showYolo()` will drive
			// authentication itself. Otherwise this is a Google Button click
			// that arrived after YOLO already settled — dispatch directly so
			// the credential is not dropped. We deliberately do NOT overwrite
			// the referrer here: it was already set either by `showYolo()`
			// (for the YOLO case) or by the button's `click_listener` (for
			// the Google Button case).
			const accepted = settle({ status: "credential", credential });
			if (!accepted) {
				void this.handleCredential(credential);
			}

		}, this.googleInstance, parentId);

		this.isGSIInitialized = true;
		globals.eventBus.emit(InternalEventType.YOLO_INITIALIZED);
	}

	private async handleCredential(credential: string): Promise<void> {
		if (this.credentialCallback) {
			await this.credentialCallback(credential);
			return;
		}

		await globalStore.actions.startGoogleYoloAuthentication(credential);
	}

	private get googleInstance(): GSIId | null {
		if (typeof google === 'undefined' || !google.accounts || !google.accounts.id) return null;

		return google.accounts.id;
	}

	private get shouldSkipSigninPrompt(): boolean {
		return !!window['OpenIdFlow'];
	}

	private static getGoogleLoginStatus(notification: GSINotification): GoogleLoginStatus {
		if (notification.isSkippedMoment()) return GoogleLoginStatus.SKIPPED;

		if (notification.getDismissedReason() === GSIDismissedReason.CREDENTIAL_RETURNED) return GoogleLoginStatus.SKIPPED;

		return GoogleLoginStatus.DISMISSED;
	}

	private isGoogleSDKAlreadyLoaded(): boolean {
		try {
			return typeof google !== 'undefined' && !!google.accounts && !!google.accounts.id;
		} catch {
			return false;
		}
	}
}
